import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAvailability, getAddons, checkout } from '../api';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const STEP_LABELS = ['Select Date', 'Service & Time', 'Add-ons', 'Review & Pay'];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {STEP_LABELS.map((label, i) => {
        const step = i + 1;
        const isActive = step === current;
        const isComplete = step < current;
        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <div
                className="w-8 sm:w-12 h-0.5 mx-1"
                style={{ backgroundColor: isComplete ? 'var(--color-accent)' : '#d1d5db' }}
              />
            )}
            <div className="flex flex-col items-center">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors"
                style={{
                  backgroundColor: isActive ? 'var(--color-accent)' : isComplete ? 'var(--color-primary)' : '#e5e7eb',
                  color: isActive || isComplete ? '#fff' : 'var(--color-text-light)',
                }}
              >
                {isComplete ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <span
                className="text-xs mt-1 hidden sm:block"
                style={{ color: isActive ? 'var(--color-accent)' : 'var(--color-text-light)' }}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ============================================================
   STEP 1 — DATE PICKER
   ============================================================ */
function StepDate({ selectedDate, onSelect }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const maxMonth = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 2);
    return { year: d.getFullYear(), month: d.getMonth() };
  }, []);

  const days = useMemo(() => {
    const { year, month } = viewMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [viewMonth]);

  const canPrev = viewMonth.year > today.getFullYear() || viewMonth.month > today.getMonth();
  const canNext = viewMonth.year < maxMonth.year || (viewMonth.year === maxMonth.year && viewMonth.month < maxMonth.month);

  const navMonth = (dir) => {
    setViewMonth((prev) => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m > 11) { m = 0; y++; }
      if (m < 0) { m = 11; y--; }
      return { year: y, month: m };
    });
  };

  const monthName = new Date(viewMonth.year, viewMonth.month).toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-primary)' }}>
        Select a Date
      </h2>

      <div className="max-w-md mx-auto rounded-xl p-5 shadow-md" style={{ backgroundColor: 'var(--color-bg-alt)' }}>
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navMonth(-1)}
            disabled={!canPrev}
            className="p-2 rounded-lg transition-colors disabled:opacity-30"
            style={{ color: 'var(--color-primary)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-semibold text-lg" style={{ color: 'var(--color-text)' }}>{monthName}</span>
          <button
            onClick={() => navMonth(1)}
            disabled={!canNext}
            className="p-2 rounded-lg transition-colors disabled:opacity-30"
            style={{ color: 'var(--color-primary)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 text-center text-xs font-medium mb-2" style={{ color: 'var(--color-text-light)' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="py-1">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} />;

            const dateObj = new Date(viewMonth.year, viewMonth.month, day);
            dateObj.setHours(0, 0, 0, 0);
            const isPast = dateObj < today;
            const dateStr = `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = selectedDate === dateStr;

            return (
              <button
                key={day}
                disabled={isPast}
                onClick={() => onSelect(dateStr)}
                className="aspect-square rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105"
                style={{
                  backgroundColor: isSelected ? 'var(--color-accent)' : 'transparent',
                  color: isSelected ? '#fff' : isPast ? 'var(--color-text-light)' : 'var(--color-text)',
                }}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   STEP 2 — SERVICE + TIME
   ============================================================ */
function StepServiceTime({ selectedDate, onSelect }) {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getAvailability(selectedDate)
      .then((data) => setAvailability(data))
      .catch(() => setError('Failed to load availability. Please try again.'))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  const formatTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (d) => {
    const date = new Date(d + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div
          className="inline-block w-8 h-8 border-4 rounded-full animate-spin"
          style={{ borderColor: 'var(--color-bg-alt)', borderTopColor: 'var(--color-accent)' }}
        />
        <p className="mt-3" style={{ color: 'var(--color-text-light)' }}>Loading availability...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-center text-red-600 py-8">{error}</p>;
  }

  const serviceEntries = availability ? Object.values(availability) : [];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
        Choose Service & Time
      </h2>
      <p className="mb-6" style={{ color: 'var(--color-text-light)' }}>{formatDate(selectedDate)}</p>

      {serviceEntries.length === 0 && (
        <p className="text-center py-8" style={{ color: 'var(--color-text-light)' }}>
          No services available. Please go back and try another date.
        </p>
      )}

      <div className="space-y-6">
        {serviceEntries.map(({ service, slots }) => (
          <div
            key={service.id}
            className="rounded-xl p-5 shadow-sm border"
            style={{ backgroundColor: 'var(--color-bg-alt)', borderColor: '#e5e7eb' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>{service.name}</h3>
                <p className="text-sm" style={{ color: 'var(--color-text-light)' }}>
                  {service.duration_hours}h session
                </p>
              </div>
              <span className="text-xl font-bold mt-2 sm:mt-0" style={{ color: 'var(--color-accent)' }}>
                ${service.price}
              </span>
            </div>

            {slots.length === 0 ? (
              <p className="text-sm italic" style={{ color: 'var(--color-text-light)' }}>
                No times available for this service on this date.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => onSelect(service, slot)}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-all hover:scale-105"
                    style={{
                      borderColor: 'var(--color-accent)',
                      color: 'var(--color-accent)',
                      backgroundColor: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--color-accent)';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = 'var(--color-accent)';
                    }}
                  >
                    {formatTime(slot)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   STEP 3 — ADD-ONS + DELIVERY
   ============================================================ */
function StepAddons({ service, selectedAddonIds, onToggleAddon, delivery, onDeliveryChange, addons, addonsLoading }) {
  if (addonsLoading) {
    return (
      <div className="text-center py-12">
        <div
          className="inline-block w-8 h-8 border-4 rounded-full animate-spin"
          style={{ borderColor: 'var(--color-bg-alt)', borderTopColor: 'var(--color-accent)' }}
        />
        <p className="mt-3" style={{ color: 'var(--color-text-light)' }}>Loading add-ons...</p>
      </div>
    );
  }

  const selectedAddonsTotal = addons
    .filter((a) => selectedAddonIds.includes(a.id))
    .reduce((sum, a) => sum + a.price, 0);

  const deliveryFee = delivery.address && delivery.distance_km > 15
    ? Math.round((delivery.distance_km - 15) * 2.5 * 100) / 100
    : 0;

  const runningTotal = service.price + selectedAddonsTotal + deliveryFee;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-primary)' }}>
        Customize Your Experience
      </h2>

      {/* Add-ons */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>Add-ons</h3>
        {addons.length === 0 ? (
          <p style={{ color: 'var(--color-text-light)' }}>No add-ons available.</p>
        ) : (
          <div className="space-y-3">
            {addons.map((addon) => {
              const checked = selectedAddonIds.includes(addon.id);
              return (
                <label
                  key={addon.id}
                  className="flex items-start gap-3 p-4 rounded-xl cursor-pointer border transition-all"
                  style={{
                    backgroundColor: checked ? 'var(--color-bg-alt)' : 'transparent',
                    borderColor: checked ? 'var(--color-accent)' : '#e5e7eb',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleAddon(addon.id)}
                    className="mt-1 w-5 h-5 rounded accent-[var(--color-accent)]"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <span className="font-medium" style={{ color: 'var(--color-text)' }}>{addon.name}</span>
                      <span className="font-semibold" style={{ color: 'var(--color-accent)' }}>+${addon.price}</span>
                    </div>
                    {addon.description && (
                      <p className="text-sm mt-0.5" style={{ color: 'var(--color-text-light)' }}>{addon.description}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Delivery */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
          Delivery <span className="text-sm font-normal" style={{ color: 'var(--color-text-light)' }}>(optional)</span>
        </h3>
        <div className="rounded-xl p-5 border" style={{ borderColor: '#e5e7eb', backgroundColor: 'var(--color-bg-alt)' }}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
              Delivery Address
            </label>
            <input
              type="text"
              value={delivery.address}
              onChange={(e) => onDeliveryChange({ ...delivery, address: e.target.value })}
              placeholder="e.g. 123 Lakeshore Dr, Penticton, BC"
              className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
              style={{
                borderColor: '#d1d5db',
                backgroundColor: 'var(--color-bg, #fff)',
                color: 'var(--color-text)',
              }}
            />
          </div>
          {delivery.address && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                Distance (km)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={delivery.distance_km}
                onChange={(e) => onDeliveryChange({ ...delivery, distance_km: Number(e.target.value) })}
                className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
                style={{
                  borderColor: '#d1d5db',
                  backgroundColor: 'var(--color-bg, #fff)',
                  color: 'var(--color-text)',
                }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-light)' }}>
                First 15 km free, then $2.50/km
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Running total */}
      <div
        className="rounded-xl p-5 border-2"
        style={{ borderColor: 'var(--color-accent)', backgroundColor: 'var(--color-bg-alt)' }}
      >
        <div className="flex justify-between text-sm mb-1" style={{ color: 'var(--color-text-light)' }}>
          <span>{service.name}</span>
          <span>${service.price.toFixed(2)}</span>
        </div>
        {addons
          .filter((a) => selectedAddonIds.includes(a.id))
          .map((a) => (
            <div key={a.id} className="flex justify-between text-sm mb-1" style={{ color: 'var(--color-text-light)' }}>
              <span>{a.name}</span>
              <span>${a.price.toFixed(2)}</span>
            </div>
          ))}
        {deliveryFee > 0 && (
          <div className="flex justify-between text-sm mb-1" style={{ color: 'var(--color-text-light)' }}>
            <span>Delivery Fee</span>
            <span>${deliveryFee.toFixed(2)}</span>
          </div>
        )}
        <div className="border-t mt-2 pt-2 flex justify-between font-bold text-lg" style={{ borderColor: '#d1d5db', color: 'var(--color-text)' }}>
          <span>Total</span>
          <span style={{ color: 'var(--color-accent)' }}>${runningTotal.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   STEP 4 — REVIEW + CHECKOUT
   ============================================================ */
function StepReview({ selectedDate, service, timeSlot, addons, selectedAddonIds, delivery, customer, guestEmail, onGuestEmailChange, onCheckout, checkoutLoading, checkoutError }) {
  const formatTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (d) => {
    const date = new Date(d + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const selectedAddons = addons.filter((a) => selectedAddonIds.includes(a.id));
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
  const deliveryFee = delivery.address && delivery.distance_km > 15
    ? Math.round((delivery.distance_km - 15) * 2.5 * 100) / 100
    : 0;
  const total = service.price + addonsTotal + deliveryFee;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-primary)' }}>
        Review Your Booking
      </h2>

      <div className="rounded-xl p-6 shadow-md mb-6" style={{ backgroundColor: 'var(--color-bg-alt)' }}>
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-light)' }}>Service</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>{service.name}</p>
            </div>
            <span className="text-lg font-bold" style={{ color: 'var(--color-accent)' }}>${service.price.toFixed(2)}</span>
          </div>

          <div>
            <p className="text-sm font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-light)' }}>Date & Time</p>
            <p className="font-semibold" style={{ color: 'var(--color-text)' }}>
              {formatDate(selectedDate)} at {formatTime(timeSlot)}
            </p>
          </div>

          {selectedAddons.length > 0 && (
            <div>
              <p className="text-sm font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-light)' }}>Add-ons</p>
              {selectedAddons.map((a) => (
                <div key={a.id} className="flex justify-between text-sm" style={{ color: 'var(--color-text)' }}>
                  <span>{a.name}</span>
                  <span>${a.price.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {delivery.address && (
            <div>
              <p className="text-sm font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-light)' }}>Delivery</p>
              <p className="text-sm" style={{ color: 'var(--color-text)' }}>{delivery.address}</p>
              <p className="text-sm" style={{ color: 'var(--color-text-light)' }}>
                {delivery.distance_km} km {deliveryFee > 0 ? `($${deliveryFee.toFixed(2)} fee)` : '(free)'}
              </p>
            </div>
          )}

          <div className="border-t pt-3 flex justify-between font-bold text-xl" style={{ borderColor: '#d1d5db' }}>
            <span style={{ color: 'var(--color-text)' }}>Total</span>
            <span style={{ color: 'var(--color-accent)' }}>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Customer info or guest email */}
      <div className="rounded-xl p-5 border mb-6" style={{ borderColor: '#e5e7eb' }}>
        {customer ? (
          <div>
            <p className="text-sm font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-light)' }}>Booking as</p>
            <p className="font-semibold" style={{ color: 'var(--color-text)' }}>{customer.name}</p>
            <p className="text-sm" style={{ color: 'var(--color-text-light)' }}>{customer.email}</p>
          </div>
        ) : (
          <div>
            <p className="text-sm font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-light)' }}>
              Guest Checkout
            </p>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
              Email Address
            </label>
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => onGuestEmailChange(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2"
              style={{
                borderColor: '#d1d5db',
                backgroundColor: 'var(--color-bg, #fff)',
                color: 'var(--color-text)',
              }}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-light)' }}>
              We'll send your booking confirmation to this email.
            </p>
          </div>
        )}
      </div>

      {checkoutError && (
        <div className="rounded-lg p-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm">
          {checkoutError}
        </div>
      )}

      <button
        onClick={onCheckout}
        disabled={checkoutLoading || (!customer && !guestEmail)}
        className="w-full py-3.5 rounded-xl text-white font-bold text-lg transition-opacity disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-accent)' }}
      >
        {checkoutLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing...
          </span>
        ) : (
          'Pay with Stripe'
        )}
      </button>
    </div>
  );
}

/* ============================================================
   MAIN BOOK NOW PAGE
   ============================================================ */
export default function BookNowPage() {
  const [searchParams] = useSearchParams();
  const { customer, token } = useCustomerAuth();

  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [delivery, setDelivery] = useState({ address: '', distance_km: 0 });
  const [guestEmail, setGuestEmail] = useState('');
  const [addons, setAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const cancelled = searchParams.get('cancelled');

  // Fetch addons when entering step 3
  useEffect(() => {
    if (step === 3) {
      setAddonsLoading(true);
      getAddons()
        .then((data) => setAddons(data))
        .catch(() => setAddons([]))
        .finally(() => setAddonsLoading(false));
    }
  }, [step]);

  const handleDateSelect = useCallback((date) => {
    setSelectedDate(date);
    setSelectedService(null);
    setSelectedTime('');
    setStep(2);
  }, []);

  const handleServiceTimeSelect = useCallback((service, time) => {
    setSelectedService(service);
    setSelectedTime(time);
    setStep(3);
  }, []);

  const handleToggleAddon = useCallback((id) => {
    setSelectedAddonIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  }, []);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const payload = {
        service_id: selectedService.id,
        booking_date: selectedDate,
        start_time: selectedTime,
        addon_ids: selectedAddonIds.length > 0 ? selectedAddonIds : undefined,
        delivery_address: delivery.address || undefined,
        delivery_distance_km: delivery.address ? delivery.distance_km : undefined,
      };

      if (customer) {
        payload.customer_id = customer.id;
      } else {
        payload.guest_email = guestEmail;
      }

      const data = await checkout(payload, token || undefined);
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      }
    } catch (err) {
      setCheckoutError(err.message || 'Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold text-center mb-2" style={{ color: 'var(--color-primary)' }}>
          Book Your Sauna
        </h1>
        <p className="text-center mb-8" style={{ color: 'var(--color-text-light)' }}>
          Reserve your mobile sauna experience in just a few steps.
        </p>

        {cancelled && (
          <div className="rounded-lg p-3 mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm text-center">
            Your previous checkout was cancelled. Feel free to try again!
          </div>
        )}

        <StepIndicator current={step} />

        {/* Back button */}
        {step > 1 && (
          <button
            onClick={goBack}
            className="flex items-center gap-1 mb-6 text-sm font-medium transition-colors"
            style={{ color: 'var(--color-text-light)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-accent)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-light)')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        )}

        {/* Steps */}
        {step === 1 && (
          <StepDate selectedDate={selectedDate} onSelect={handleDateSelect} />
        )}
        {step === 2 && (
          <StepServiceTime selectedDate={selectedDate} onSelect={handleServiceTimeSelect} />
        )}
        {step === 3 && (
          <div>
            <StepAddons
              service={selectedService}
              selectedAddonIds={selectedAddonIds}
              onToggleAddon={handleToggleAddon}
              delivery={delivery}
              onDeliveryChange={setDelivery}
              addons={addons}
              addonsLoading={addonsLoading}
            />
            <button
              onClick={() => setStep(4)}
              className="w-full mt-6 py-3 rounded-xl text-white font-bold text-lg transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'var(--color-accent)' }}
            >
              Continue to Review
            </button>
          </div>
        )}
        {step === 4 && (
          <StepReview
            selectedDate={selectedDate}
            service={selectedService}
            timeSlot={selectedTime}
            addons={addons}
            selectedAddonIds={selectedAddonIds}
            delivery={delivery}
            customer={customer}
            guestEmail={guestEmail}
            onGuestEmailChange={setGuestEmail}
            onCheckout={handleCheckout}
            checkoutLoading={checkoutLoading}
            checkoutError={checkoutError}
          />
        )}
      </div>
    </div>
  );
}
